import { CameraFilled } from '@ant-design/icons';
import {
  BillDetails,
  cubicMeters,
  describeError,
  dirhams,
  FINAL_STATUSES,
  fullDate,
  isGeneratedMeterNo,
  METER_ERRORS,
  METER_STATUS_ERRORS,
  METER_STATUS_HINTS,
  METER_STATUS_LABELS,
  type MeterStatus,
  meterNoOrGenerated,
  monthYear,
  nextSortOrder,
  nextStatuses,
  PhotoField,
  present,
  todayInMorocco,
  toWesternDigits,
} from '@horof/shared';
import {
  Alert,
  App,
  Button,
  Input,
  Modal,
  Radio,
  Select,
  Skeleton,
  Tag,
} from 'antd';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery } from 'urql';
import { DeskPanel, DocumentViewer, useDirty } from './panel';
import {
  ChangeMeterStatusDocument,
  CreateMeterDocument,
  MemberChoicesDocument,
  type MeterDetails,
  MeterDocument,
  MetersDocument,
  peopleContext,
  UpdateMeterDocument,
} from './people-data';
import { useIsCommittee } from './water-data';

type Values = {
  memberId: string | null;
  meterNo: string;
  meterAddress: string;
};

const FIELD_LABELS: Record<keyof Values, string> = {
  memberId: 'العضو',
  meterNo: 'الرقم',
  meterAddress: 'العنوان',
};

/** A meter: who and where, its status, photo, history and readings. */
export function MeterPanel({
  id,
  onClose,
  onOpen,
  onDirtyChange,
  inline = false,
  formOnly = false,
  onDone,
}: {
  /** Inside the meter's page (doc 58): its header shows the summary. */
  inline?: boolean;
  /** Only the form, inside a page's section (doc 60). */
  formOnly?: boolean;
  /** After saving or «إلغاء» (formOnly). */
  onDone?: () => void;
  id: string | null;
  onClose: () => void;
  onOpen: (id: string) => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const [{ data }] = useQuery({
    query: MeterDocument,
    variables: { id: id ?? '' },
    pause: !id,
    context: useMemo(() => peopleContext, []),
  });
  const meter = data?.meter ?? null;
  if (id && !meter) {
    return (
      <DeskPanel title="العداد" onClose={onClose} inline={inline}>
        {data ? (
          <Alert type="warning" showIcon message="العداد غير موجود" />
        ) : (
          <Skeleton active paragraph={{ rows: 10 }} />
        )}
      </DeskPanel>
    );
  }
  return (
    <MeterEditor
      meter={meter}
      inline={inline}
      formOnly={formOnly}
      onDone={onDone}
      onClose={onClose}
      onOpen={onOpen}
      onDirtyChange={onDirtyChange}
    />
  );
}

function MeterEditor({
  meter,
  inline,
  formOnly,
  onDone,
  onClose,
  onOpen,
  onDirtyChange,
}: {
  inline: boolean;
  formOnly: boolean;
  onDone?: () => void;
  meter: MeterDetails | null;
  onClose: () => void;
  onOpen: (id: string) => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const canEdit = useIsCommittee();
  const { modal, message } = App.useApp();
  const [params] = useSearchParams();
  const [{ data: choices }] = useQuery({ query: MemberChoicesDocument });
  const [{ data: all }] = useQuery({ query: MetersDocument });
  const generated = meter ? isGeneratedMeterNo(meter.meterNo) : false;
  const [saved, setSaved] = useState<Values>(
    meter
      ? {
          memberId: meter.memberId,
          meterNo: generated ? '' : meter.meterNo,
          meterAddress: meter.meterAddress,
        }
      : { memberId: params.get('member'), meterNo: '', meterAddress: '' },
  );
  const [values, setValues] = useState(saved);
  const [photoId, setPhotoId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [changingStatus, setChangingStatus] = useState(false);
  const [bill, setBill] = useState<string | null>(null);
  const [{ fetching: creating }, create] = useMutation(CreateMeterDocument);
  const [{ fetching: updating }, update] = useMutation(UpdateMeterDocument);
  useDirty({ ...values, photoId }, { ...saved, photoId: null }, onDirtyChange);
  const set = (patch: Partial<Values>) =>
    setValues((v) => ({ ...v, ...patch }));
  // The meter's member stays listed even if they left since.
  const members = present(choices?.members?.nodes);
  const options = members.map((m) => ({
    value: m.id,
    label: m.fullName ?? '',
    search: `${m.fullName} ${m.memberAddress}`,
  }));
  if (meter?.member && !members.some((m) => m.id === meter.memberId)) {
    options.push({
      value: meter.member.id,
      label: meter.member.fullName ?? '',
      search: meter.member.fullName ?? '',
    });
  }

  async function save() {
    setProblem(null);
    if (!values.memberId) return setProblem('اختر العضو.');
    if (!values.meterAddress.trim()) return setProblem('اكتب العنوان.');
    if (!meter) {
      if (!photoId) return setProblem(METER_ERRORS.METER_PHOTO_REQUIRED);
      const r = await create(
        {
          meter: {
            memberId: values.memberId,
            meterNo: meterNoOrGenerated(values.meterNo),
            meterAddress: values.meterAddress.trim(),
            meterStatus: 'CONNECTED',
            photoId,
            // Last in the round (arqam's default, 0, would put it first).
            sortOrder: nextSortOrder(
              present(all?.meters?.nodes).map((m) => m.sortOrder),
            ),
          },
        },
        peopleContext,
      );
      const createdId = r.data?.createMeter?.meter?.id;
      if (r.error || !createdId) {
        setProblem(describeError(r.error, 'تعذّر إضافة العداد.', METER_ERRORS));
        return;
      }
      message.success('أُضيف العداد، في آخر الجولة');
      onDirtyChange(false);
      onOpen(createdId);
      return;
    }
    const fields = {
      memberId: values.memberId,
      meterNo: values.meterNo.trim()
        ? values.meterNo.trim()
        : generated
          ? meter.meterNo
          : meterNoOrGenerated(''),
      meterAddress: values.meterAddress.trim(),
      ...(photoId ? { photoId } : {}),
    };
    const before = meter as Record<string, unknown>;
    const changes = Object.entries(fields).filter(
      ([key, value]) => value !== before[key],
    );
    if (!changes.length) {
      message.info('لا تغيير');
      onDone?.();
      return;
    }
    const name = (memberId: unknown) =>
      options.find((o) => o.value === memberId)?.label ?? '؟';
    const ok = await modal.confirm({
      title: `حفظ العداد ${meter.label}`,
      width: 520,
      content: (
        <table className="desk-changes">
          <tbody>
            {changes.map(([key, value]) => (
              <tr key={key}>
                <td>
                  {key === 'photoId'
                    ? 'الصورة'
                    : FIELD_LABELS[key as keyof Values]}
                </td>
                <td className="is-before">
                  {key === 'memberId'
                    ? name(before[key])
                    : key === 'photoId'
                      ? meter.photo
                        ? 'القديمة'
                        : '—'
                      : key === 'meterNo' && generated
                        ? 'بدون رقم'
                        : String(before[key] ?? '—')}
                </td>
                <td className="is-after">
                  {key === 'memberId'
                    ? name(value)
                    : key === 'photoId'
                      ? 'الجديدة'
                      : key === 'meterNo' && isGeneratedMeterNo(String(value))
                        ? 'بدون رقم'
                        : String(value)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ),
      okText: 'حفظ',
      cancelText: 'رجوع',
    });
    if (!ok) return;
    const r = await update(
      { id: meter.id, patch: Object.fromEntries(changes) },
      peopleContext,
    );
    if (r.error) {
      setProblem(describeError(r.error, 'تعذّر حفظ العداد.', METER_ERRORS));
      return;
    }
    setSaved(values);
    setPhotoId(null);
    message.success('حُفظ العداد');
    onDirtyChange(false);
    onDone?.();
  }

  const changes = present(meter?.meterStatusChanges.nodes);
  const bills = present(meter?.recentBills.nodes);

  const formView = (
    <section className="desk-panel-section">
      <div className="desk-fields">
        <label htmlFor="meter-member">العضو</label>
        <label htmlFor="meter-no">رقم العداد (إن وُجد)</label>
        <Select
          id="meter-member"
          showSearch
          disabled={!canEdit}
          placeholder="ابحث بالاسم"
          optionFilterProp="search"
          value={values.memberId ?? undefined}
          onChange={(memberId) => set({ memberId })}
          options={options}
        />
        <Input
          id="meter-no"
          className="ltr-num"
          disabled={!canEdit}
          placeholder="فارغ: يُعرف بعنوانه"
          value={values.meterNo}
          onChange={(e) => set({ meterNo: e.target.value })}
        />
      </div>
      <label htmlFor="meter-address">العنوان أو الاسم المعروف به</label>
      <Input
        id="meter-address"
        disabled={!canEdit}
        placeholder="مثلاً «محمد كرومي _ 12» أو «درب المسجد»"
        value={values.meterAddress}
        onChange={(e) => set({ meterAddress: e.target.value })}
      />
      {canEdit && (
        <PhotoField
          kind="METER_READING"
          label={
            meter
              ? meter.photo
                ? 'تغيير الصورة'
                : 'إضافة صورة'
              : 'صورة العداد (الرقم والقراءة الأولى)'
          }
          required={!meter}
          hint="صورة واضحة للرقم والقراءة: اسحب ملفاً هنا أو الصقه (Ctrl+V)."
          value={photoId}
          onChange={setPhotoId}
          onBusyChange={setUploading}
          pasteFromClipboard
        />
      )}
      {canEdit && (
        <div className="desk-actions">
          <Button
            type="primary"
            loading={creating || updating}
            disabled={uploading}
            onClick={save}
            title="Ctrl+S"
          >
            {meter ? 'حفظ' : 'إضافة العداد'}
          </Button>
          {onDone && (
            <Button
              onClick={() => {
                onDirtyChange(false);
                onDone();
              }}
            >
              إلغاء
            </Button>
          )}
        </div>
      )}
    </section>
  );

  if (formOnly) {
    return (
      <div className="ui-form">
        {problem && <Alert type="error" showIcon message={problem} />}
        {formView}
      </div>
    );
  }

  return (
    <DeskPanel
      title={meter ? `العداد ${meter.label}` : 'عداد جديد'}
      recordId={meter?.id}
      onClose={onClose}
      onSave={canEdit ? save : undefined}
      inline={inline}
    >
      {meter && !inline && (
        <div className="desk-panel-tags">
          <Tag color={meter.meterStatus === 'CONNECTED' ? 'green' : 'default'}>
            {METER_STATUS_LABELS[meter.meterStatus]}
          </Tag>
          {meter.member && (
            <Link to={`/members/${meter.member.id}`}>
              {meter.member.fullName}
            </Link>
          )}
          <span className="field-hint">منذ {fullDate(meter.connectedAt)}</span>
          <Link to={`/bills?meter=${meter.id}`}>فواتير العداد</Link>
        </div>
      )}
      {meter?.photo && (
        <DocumentViewer photo={meter.photo} title="صورة العداد" />
      )}
      {problem && <Alert type="error" showIcon message={problem} />}

      {formView}

      {meter && (
        <>
          <section className="desk-panel-section">
            <h3>الحالة</h3>
            <div>
              <Tag
                color={meter.meterStatus === 'CONNECTED' ? 'green' : 'default'}
              >
                {METER_STATUS_LABELS[meter.meterStatus]}
              </Tag>
              {meter.statusReason && (
                <span className="field-hint">
                  {meter.statusReason}
                  {meter.statusChangedAt &&
                    ` (${fullDate(meter.statusChangedAt)})`}
                </span>
              )}
            </div>
            {meter.meterStatus !== 'CONNECTED' && (
              <div className="field-hint">
                غير المربوط لا يظهر في الجولة ولا تصدر له فواتير.
              </div>
            )}
            {canEdit && nextStatuses(meter.meterStatus).length > 0 && (
              <Button size="small" onClick={() => setChangingStatus(true)}>
                تغيير الحالة
              </Button>
            )}
            {changes.map((c) => (
              <div key={c.id} className="field-hint">
                {fullDate(c.changedOn)}:{' '}
                {c.fromStatus ? `${METER_STATUS_LABELS[c.fromStatus]} ← ` : ''}
                {METER_STATUS_LABELS[c.toStatus]} · {c.reason}
                {c.reading !== null && ` · القراءة ${c.reading}`}
                {c.meterByReplacedBy &&
                  ` · العداد الجديد: ${c.meterByReplacedBy.label}`}
                {c.changedBy && ` · ${c.changedBy}`}
              </div>
            ))}
          </section>

          {!inline && (
            <section className="desk-panel-section">
              <h3>آخر القراءات</h3>
              {bills.length === 0 && (
                <div className="field-hint">لا قراءة بعد.</div>
              )}
              <table className="desk-mini-table">
                <tbody>
                  {bills.map((b) => (
                    <tr
                      key={b.id}
                      className="is-clickable"
                      onClick={() => setBill(b.id)}
                    >
                      <td>
                        {monthYear(b.billMonth)}
                        {b.readingPhoto && (
                          <CameraFilled
                            className="receipt-mark"
                            aria-label="مع صورة العداد"
                          />
                        )}
                      </td>
                      <td className="ltr-num">{b.currentReading}</td>
                      <td>{cubicMeters(b.consumption)}</td>
                      <td>{dirhams(b.billTotal)}</td>
                      <td>
                        {b.billStatus !== 'PAID' && (
                          <Tag
                            color={
                              b.billStatus === 'OVERDUE' ? 'red' : 'orange'
                            }
                          >
                            {b.billStatus === 'OVERDUE'
                              ? 'متأخرة'
                              : 'غير مؤداة'}
                          </Tag>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}

      {changingStatus && meter && (
        <MeterStatusDialog
          meter={meter}
          onClose={() => setChangingStatus(false)}
        />
      )}
      {bill && (
        <Modal
          open
          width={560}
          footer={null}
          title="تفاصيل الفاتورة"
          onCancel={() => setBill(null)}
        >
          <BillDetails
            billId={bill}
            memberLink={(memberId) => `/members/${memberId}`}
          />
        </Modal>
      )}
    </DeskPanel>
  );
}

/** arqam's appChangeMeterStatus (doc 41): reason, date, last reading. */
export function MeterStatusDialog({
  meter,
  onClose,
}: {
  meter: MeterDetails;
  onClose: () => void;
}) {
  const { message } = App.useApp();
  const [{ fetching }, change] = useMutation(ChangeMeterStatusDocument);
  const options = nextStatuses(meter.meterStatus);
  const [status, setStatus] = useState<MeterStatus | null>(null);
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(todayInMorocco());
  const [reading, setReading] = useState('');
  const [replacedBy, setReplacedBy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const others = present(meter.member?.meters.nodes).filter(
    (m) => m.id !== meter.id,
  );

  async function save() {
    const readingValue = toWesternDigits(reading).trim();
    if (!status) return setProblem('اختر الحالة الجديدة.');
    if (!reason.trim()) return setProblem(METER_STATUS_ERRORS.REASON_REQUIRED);
    if (readingValue && !/^\d+$/.test(readingValue)) {
      return setProblem('القراءة أرقام فقط.');
    }
    if (status === 'REPLACED' && !replacedBy) {
      return setProblem(METER_STATUS_ERRORS.REPLACEMENT_REQUIRED);
    }
    const r = await change(
      {
        input: {
          meterId: meter.id,
          status,
          reason: reason.trim(),
          changedOn: date || null,
          reading: readingValue ? Number(readingValue) : null,
          replacedBy: status === 'REPLACED' ? replacedBy : null,
        },
      },
      peopleContext,
    );
    if (r.error) {
      setProblem(
        describeError(r.error, 'تعذّر تغيير الحالة.', METER_STATUS_ERRORS),
      );
      return;
    }
    message.success(`أصبح العداد «${METER_STATUS_LABELS[status]}»`);
    onClose();
  }

  return (
    <Modal
      open
      width={560}
      title={`تغيير حالة العداد ${meter.label}`}
      okText="حفظ"
      okButtonProps={{ danger: !!status && FINAL_STATUSES.includes(status) }}
      cancelText="إلغاء"
      confirmLoading={fetching}
      onOk={save}
      onCancel={onClose}
    >
      <div className="desk-dialog">
        <div className="field-hint">
          الحالة الحالية: {METER_STATUS_LABELS[meter.meterStatus]}
        </div>
        <Radio.Group
          className="desk-radio-column"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          options={options.map((s) => ({
            value: s,
            label: (
              <span>
                <strong>{METER_STATUS_LABELS[s]}</strong>
                <span className="field-hint"> — {METER_STATUS_HINTS[s]}</span>
              </span>
            ),
          }))}
        />
        {status && FINAL_STATUSES.includes(status) && (
          <Alert
            type="warning"
            showIcon
            message="حالة نهائية: لا يمكن الرجوع عنها بعد الحفظ."
          />
        )}
        {status === 'REPLACED' && (
          <Select
            placeholder="العداد الجديد"
            value={replacedBy ?? undefined}
            onChange={setReplacedBy}
            options={others.map((m) => ({ value: m.id, label: m.label ?? '' }))}
            notFoundContent="أضف العداد الجديد للعضو أولاً (مربوطاً)."
          />
        )}
        <Input.TextArea
          autoSize={{ minRows: 2 }}
          placeholder="السبب (مطلوب)، مثلاً: عدم الأداء منذ 4 أشهر"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <div className="desk-dialog-row">
          <div className="desk-dialog-field">
            <label htmlFor="status-date">تاريخ التغيير</label>
            <Input
              id="status-date"
              type="date"
              className="ltr-num"
              max={todayInMorocco()}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="desk-dialog-field">
            <label htmlFor="status-reading">آخر قراءة (اختيارية)</label>
            <Input
              id="status-reading"
              className="ltr-num"
              value={reading}
              onChange={(e) => setReading(e.target.value)}
            />
          </div>
        </div>
        {problem && <div className="field-error">{problem}</div>}
      </div>
    </Modal>
  );
}
